import { useMemo } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { Icon } from '@iconify/react'

import { Modal, Button } from '@pepper/ui'
import { getAuditPropertyKeys } from '@pepper/utils/src/function/audits'
import { useGetMarketplaceAuditStatus } from '@pepper/docs/src/hooks/api/content-audit/useGetMarketplaceAuditStatus'

import styles from '../_.module.css'

type AuditConfirmationModalProps = {
  assignmentId: string
  userRole: CreatorRole
  metricsArray: string[]
  handleSubmit: (type: ButtonActionKeys | null) => void
  onCancel: () => void
}

const AuditConfirmationModal = NiceModal.create<AuditConfirmationModalProps>(
  ({ assignmentId, userRole, metricsArray, handleSubmit, onCancel }) => {
    const auditStatus = useGetMarketplaceAuditStatus({
      data: { itemId: assignmentId },
    })
    const { visible: isModalVisible, remove: removeModal } = useModal()
    const { show: showAuditModalV2 } = useModal('audit-v2')

    const [auditsKey, auditLimitKey] = useMemo(() => {
      return getAuditPropertyKeys(auditStatus.data, userRole)
    }, [auditStatus.data, userRole])

    const handleContinueClick = () => {
      removeModal()
      onCancel()
      showAuditModalV2({
        assignmentId,
        metricsArray,
        handleSubmit: (type: ButtonActionKeys) => {
          handleSubmit(type)
        },
        onCancel: () => {
          onCancel()
        },
      })
    }

    const auditAttemptsLeft = useMemo(() => {
      if (!auditStatus.data) return null
      return (
        auditStatus.data[auditLimitKey] - auditStatus.data[auditsKey].length
      )
    }, [auditLimitKey, auditStatus.data, auditsKey])

    const [_, auditLimit] = getAuditPropertyKeys(auditStatus?.data, userRole)

    if (auditAttemptsLeft === 0) {
      return (
        <Modal
          wrapClassName={clsx(styles.auditModal, styles.auditModalV2)}
          title={null}
          footer={null}
          visible={isModalVisible}
          closeIcon={<Icon icon="lucide:x" />}
          onCancel={removeModal}
        >
          <div className={styles.modalTitle}>Review & Submit</div>
          <div className={styles.modalBodyContainer}>
            <div className={styles.confirmationText}>
              You have no review attempt left for your assignment.
            </div>
            <div
              className={clsx(
                styles.footerButtonGroup,
                styles.noAttemptLeftFooter
              )}
            >
              <div className={styles.reviewsLeft}>
                <div className={styles.greenPill} />
                Reviews: 0/{auditStatus.data?.[auditLimit]}
              </div>
              <div className={styles.buttonContainer}>
                <Button
                  onClick={() => {
                    removeModal()
                    onCancel()
                  }}
                  className={styles.dismissModalButton}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )
    }

    const reviewAttemptsLeft =
      auditAttemptsLeft === 2 ? (
        <>
          up to <span className={styles.bold}>two review attempts</span>
        </>
      ) : (
        <span className={styles.bold}>one review attempt</span>
      )

    return (
      <Modal
        wrapClassName={clsx(styles.auditModal, styles.auditModalV2)}
        title={null}
        footer={null}
        visible={isModalVisible}
        closeIcon={<Icon icon="lucide:x" />}
        onCancel={removeModal}
      >
        <div className={styles.modalTitle}>Review & Submit</div>
        <div className={styles.modalBodyContainer}>
          <div className={styles.confirmationText}>
            You have {reviewAttemptsLeft} for your assignment. Please ensure
            your submission meets the required standards for grammar,
            readability, word count, and originality.
          </div>
          {auditAttemptsLeft === 1 && (
            <div
              className={clsx(styles.warningText, styles.confirmationWarning)}
            >
              <div className={styles.alertIcon}>
                <Icon icon="lucide:triangle-alert" />
              </div>
              This is your last opportunity to review. Please make sure all
              feedback from prior reviews has been resolved.
            </div>
          )}
        </div>
        <div
          className={clsx(
            styles.footerButtonGroup,
            styles.auditConfirmationFooter,
            {
              [styles.applyBorder]: auditAttemptsLeft === 1,
            }
          )}
        >
          <div className={styles.reviewsLeft}>
            <div className={styles.greenPill} />
            Reviews: {auditAttemptsLeft}/{auditStatus.data?.[auditLimit]}
          </div>
          <div className={styles.buttonContainer}>
            <Button
              onClick={() => {
                removeModal()
                onCancel()
              }}
              className={styles.dismissModalButton}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleContinueClick}
              className={styles.continueButton}
            >
              Continue
            </Button>
          </div>
        </div>
      </Modal>
    )
  }
)

export default AuditConfirmationModal
